import styled from '@emotion/styled'
import { COLORS } from '@borrowell/bw-styles'
import { Link } from 'gatsby'
import React from 'react'

import { HREFLANG, Locale, useLocale } from '../context/LocaleContextProvider'
import { saveLanguagePreference } from '../helpers/languagePreference'
import { useSendLinkClickedEvent } from '../hooks/analytics/useSendLinkClickedEvent'
import { FONT_FAMILY } from '../styles/theme'

const TOGGLE_LOCALES: { locale: Locale; text: string; lang: string; label: string }[] = [
  { locale: 'en-CA', text: 'EN', lang: 'en', label: 'English' },
  { locale: 'fr-CA', text: 'FR', lang: 'fr', label: 'Français' },
]

const Toggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${FONT_FAMILY.LATO};
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0.2px;
  color: ${COLORS.NEUTRAL.COOL['900']};
  white-space: nowrap;
`

const CurrentLocale = styled.span`
  font-weight: bold;
`

// Gatsby's Link, not ours: ours rewrites links to their French version on French pages, which would turn the
// "EN" link back into the French page.
const OtherLocale = styled(Link)`
  color: inherit;
  font-weight: normal;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

/**
 * @description "EN | FR": the current language in bold, the other one linking to the same page in that language.
 * Renders nothing when the page is built in one locale only (English-only pages, or the flag off).
 * Clicking a language saves it as the visitor's preference.
 */
export const LanguageToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { locale, alternates } = useLocale()
  const sendLinkClickedEvent = useSendLinkClickedEvent()
  if (Object.keys(alternates).length < 2) return null

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, toLocale: Locale, to: string, text: string) => {
    // Remembered so later visits to English pages open their French version (LanguagePreferenceGate). Saved before
    // navigating, so switching to English doesn't bounce back to French.
    saveLanguagePreference(toLocale)
    // Same header context the Link component reports, so toggle clicks segment like other header links.
    const headerVersion = event.currentTarget.closest('[data-header-version]')?.getAttribute('data-header-version')
    const position = event.currentTarget.closest('[data-header-position]')?.getAttribute('data-header-position')
    sendLinkClickedEvent({
      entry_id: 'N/A',
      contentful_type: 'LanguageToggle',
      link_to: to,
      link_text: text,
      ...(headerVersion && { header_version: headerVersion }),
      ...(position && { position }),
    })
  }

  return (
    <Toggle className={className} data-cy="language-toggle">
      {TOGGLE_LOCALES.map(({ locale: toggleLocale, text, lang, label }, index) => {
        // Both locales are in alternates here: the toggle returns early otherwise.
        const to = alternates[toggleLocale] as string
        return (
          <React.Fragment key={toggleLocale}>
            {index > 0 && <span aria-hidden="true">|</span>}
            {toggleLocale === locale ? (
              <CurrentLocale lang={lang} aria-current="true" aria-label={label}>
                {text}
              </CurrentLocale>
            ) : (
              <OtherLocale
                to={to}
                lang={lang}
                hrefLang={HREFLANG[toggleLocale]}
                aria-label={label}
                onClick={event => handleClick(event, toggleLocale, to, text)}
              >
                {text}
              </OtherLocale>
            )}
          </React.Fragment>
        )
      })}
    </Toggle>
  )
}
